import { useState, useId } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { T } from "../tokens";
import { Btn } from "./Btn";

const MAX_SECONDARY_ROLES = 3;
const SENIORITY_LEVELS = ["junior", "mid", "senior", "staff", "principal", "vp", "c-level"] as const;
// The subset of WORK_AUTH_TAGS (Profile.tsx) that actually describes work
// MODE — the rest (oci-friendly, relocation-sponsored, h1b-transfer) are visa/
// sponsorship tags, a separate concern this editor deliberately leaves alone.
const WORK_MODE_TAGS = [
  { value: "",                  label: "No preference" },
  { value: "remote-from-india", label: "Remote" },
  { value: "hybrid-india",      label: "Hybrid" },
  { value: "india-onsite",      label: "Onsite" },
  { value: "contract-remote",   label: "Contract (remote)" },
] as const;

/** The work-mode tag values, exported so callers (Profile.tsx) can split a
 *  resume's full workAuthTags into "mode" vs. "other" (visa/sponsorship)
 *  using the SAME list this editor edits, rather than a second copy of it. */
export const WORK_MODE_TAG_VALUES: readonly string[] = WORK_MODE_TAGS
  .map((t) => t.value)
  .filter((v) => v !== "");

export interface TargetRoleInitial {
  primaryRole:     string;
  secondaryRoles:  string[];
  locations:       string[];
  workMode:        string;   // one of WORK_MODE_TAGS.value, or ""
  seniorityLevel:  string;   // one of SENIORITY_LEVELS, or ""
  /** Non-work-mode tags (visa/sponsorship) to preserve verbatim on save. */
  otherWorkAuthTags: string[];
}

export interface TargetRolePayload {
  targetRoles:      string[];
  targetLocations:  string[];
  workAuthTags:     string[];
  seniorityLevel:   string | null;
}

const inputStyle: React.CSSProperties = {
  width: "100%", boxSizing: "border-box", padding: "9px 11px",
  background: T.s2, border: `1px solid ${T.b1}`, borderRadius: "7px",
  color: T.t0, fontFamily: T.body, fontSize: "13px", outline: "none",
};
const labelStyle: React.CSSProperties = {
  fontFamily: T.mono, fontSize: "10px", color: T.t3,
  letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "6px", display: "block",
};

/** Shared Job Preferences / Target Role form — used on Profile.tsx and in the
 *  post-upload role-confirmation step (ResumeOnboarding.tsx). Owns its own
 *  PATCH /resumes/preferences mutation so both call sites get identical
 *  success/failure handling instead of duplicating fetch logic. */
export function TargetRoleEditor({
  initial,
  primaryInputRef,
  onSaved,
  saveLabel = "Save preferences",
}: {
  initial: TargetRoleInitial;
  /** Lets a caller (e.g. the Matches CTA target) programmatically focus this field. */
  primaryInputRef?: React.RefObject<HTMLInputElement | null>;
  onSaved?: (payload: TargetRolePayload) => void;
  saveLabel?: string;
}) {
  const qc = useQueryClient();
  const idBase = useId();

  const [primary, setPrimary]   = useState(initial.primaryRole);
  const [secondary, setSecondary] = useState<string[]>(initial.secondaryRoles.slice(0, MAX_SECONDARY_ROLES));
  const [secondaryDraft, setSecondaryDraft] = useState("");
  const [locations, setLocations] = useState<string[]>(initial.locations);
  const [locationDraft, setLocationDraft] = useState("");
  const [workMode, setWorkMode] = useState(initial.workMode);
  const [seniority, setSeniority] = useState(initial.seniorityLevel);

  const mutation = useMutation({
    mutationFn: async () => {
      const payload: TargetRolePayload = {
        targetRoles:     [primary.trim(), ...secondary].filter(Boolean),
        targetLocations: locations,
        workAuthTags:    [...(workMode ? [workMode] : []), ...initial.otherWorkAuthTags],
        seniorityLevel:  seniority || null,
      };
      const { data } = await axios.patch("/api/resumes/preferences", payload);
      return { data, payload };
    },
    onSuccess: ({ payload }) => {
      qc.invalidateQueries({ queryKey: ["resume", "active"] });
      qc.invalidateQueries({ queryKey: ["active-resume"] });
      // A meaningful preference change invalidates the backend's stale match
      // set (see PATCH /resumes/preferences) — without refetching these, the
      // Dashboard/Matches UI would keep showing the old (now possibly stale)
      // matches/readiness state until some unrelated refetch happened to fire.
      qc.invalidateQueries({ queryKey: ["candidate-readiness"] });
      qc.invalidateQueries({ queryKey: ["matches"] });
      qc.invalidateQueries({ queryKey: ["matches-page"] });
      qc.invalidateQueries({ queryKey: ["match-stats"] });
      onSaved?.(payload);
    },
  });

  const canSave = primary.trim().length > 0 && !mutation.isPending;

  function addSecondary() {
    const v = secondaryDraft.trim();
    if (!v || secondary.length >= MAX_SECONDARY_ROLES || secondary.includes(v)) return;
    setSecondary((s) => [...s, v]);
    setSecondaryDraft("");
  }
  function addLocation() {
    const v = locationDraft.trim();
    if (!v || locations.includes(v)) return;
    setLocations((l) => [...l, v]);
    setLocationDraft("");
  }

  const chipStyle: React.CSSProperties = {
    display: "inline-flex", alignItems: "center", gap: "6px",
    padding: "4px 6px 4px 10px", background: T.tealBg, border: `1px solid ${T.tealBdr}`,
    borderRadius: "99px", fontFamily: T.body, fontSize: "12px", color: T.tealSoft,
  };
  const removeBtn = (onClick: () => void, label: string) => (
    <button type="button" onClick={onClick} aria-label={label}
      style={{ background: "none", border: "none", color: T.tealSoft, cursor: "pointer",
        fontFamily: T.body, fontSize: "13px", lineHeight: 1, padding: 0 }}>
      ×
    </button>
  );

  return (
    <div>
      <div style={{ marginBottom: "14px" }}>
        <label htmlFor={`${idBase}-primary`} style={labelStyle}>Primary target role (required)</label>
        <input
          id={`${idBase}-primary`}
          ref={primaryInputRef}
          type="text"
          value={primary}
          onChange={(e) => setPrimary(e.target.value)}
          placeholder="e.g. Applied AI Solutions Architect"
          style={inputStyle}
          required
        />
      </div>

      <div style={{ marginBottom: "14px" }}>
        <label htmlFor={`${idBase}-secondary`} style={labelStyle}>
          Secondary target roles (optional, up to {MAX_SECONDARY_ROLES})
        </label>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: secondary.length ? "8px" : 0 }}>
          {secondary.map((r) => (
            <span key={r} style={chipStyle}>
              {r}
              {removeBtn(() => setSecondary((s) => s.filter((x) => x !== r)), `Remove ${r}`)}
            </span>
          ))}
        </div>
        {secondary.length < MAX_SECONDARY_ROLES && (
          <div style={{ display: "flex", gap: "6px" }}>
            <input
              id={`${idBase}-secondary`}
              type="text"
              value={secondaryDraft}
              onChange={(e) => setSecondaryDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSecondary(); } }}
              placeholder="e.g. Solutions Architect"
              style={inputStyle}
            />
            <Btn variant="outline" size="sm" type="button" onClick={addSecondary} disabled={!secondaryDraft.trim()}>Add</Btn>
          </div>
        )}
        <p style={{ fontFamily: T.body, fontSize: "11px", color: T.t3, margin: "6px 0 0", lineHeight: 1.5 }}>
          Keep secondary roles closely related to your primary role — a wide mix of unrelated
          fields makes matching less precise, not more.
        </p>
      </div>

      <div style={{ marginBottom: "14px" }}>
        <label htmlFor={`${idBase}-location`} style={labelStyle}>Preferred locations</label>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: locations.length ? "8px" : 0 }}>
          {locations.map((l) => (
            <span key={l} style={chipStyle}>
              {l}
              {removeBtn(() => setLocations((ls) => ls.filter((x) => x !== l)), `Remove ${l}`)}
            </span>
          ))}
        </div>
        <div style={{ display: "flex", gap: "6px" }}>
          <input
            id={`${idBase}-location`}
            type="text"
            value={locationDraft}
            onChange={(e) => setLocationDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addLocation(); } }}
            placeholder="e.g. Bengaluru"
            style={inputStyle}
          />
          <Btn variant="outline" size="sm" type="button" onClick={addLocation} disabled={!locationDraft.trim()}>Add</Btn>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "16px" }}>
        <div>
          <label htmlFor={`${idBase}-workmode`} style={labelStyle}>Remote / hybrid preference</label>
          <select id={`${idBase}-workmode`} value={workMode} onChange={(e) => setWorkMode(e.target.value)} style={inputStyle}>
            {WORK_MODE_TAGS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor={`${idBase}-seniority`} style={labelStyle}>Seniority level</label>
          <select id={`${idBase}-seniority`} value={seniority} onChange={(e) => setSeniority(e.target.value)} style={inputStyle}>
            <option value="">Not specified</option>
            {SENIORITY_LEVELS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
        <Btn type="button" variant="cta" size="sm" onClick={() => mutation.mutate()} disabled={!canSave}>
          {mutation.isPending ? "Saving…" : saveLabel}
        </Btn>
        <div role="status" aria-live="polite">
          {mutation.isError && (
            <span role="alert" style={{ fontFamily: T.body, fontSize: "12px", color: T.err }}>
              Could not save your preferences. Please try again.
            </span>
          )}
          {mutation.isSuccess && (
            <span style={{ fontFamily: T.body, fontSize: "12px", color: T.ok }}>Saved.</span>
          )}
        </div>
      </div>
    </div>
  );
}
